import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const pillars = [
  { label: 'Strong', desc: 'Building physical and emotional resilience so every girl is prepared to face life\'s challenges with confidence.' },
  { label: 'Smart', desc: 'Encouraging academic curiosity and critical thinking, helping girls see themselves as capable learners and leaders.' },
  { label: 'Bold', desc: 'Empowering girls to speak up, take risks, and embrace their individuality without hesitation.' },
];

const skills = [
  'Building self-esteem and confidence',
  'Developing leadership skills',
  'Fostering resilience and grit',
  'Creating a sense of sisterhood',
  'Encouraging individuality',
  'Developing essential life skills',
  'Safe space for open exploration',
  'Personal and academic growth',
];

const GirlsIncPage = () => (
  <div className="min-h-screen bg-white">
    <Navbar />

    <main id="main-content" className="pt-0">
      {/* Hero */}
      <section
        className="relative pt-36 pb-20 bg-[#00213a] overflow-hidden"
      >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <span className="text-sm font-semibold text-white tracking-tight">Student Life</span>
            </div>
          </div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Girls Inc.
          </h1>
          <p className="text-2xl text-white/90 font-normal max-w-3xl leading-relaxed mx-auto">
            Inspiring and supporting young girls in grades 3–6 to be strong, smart, and bold.
          </p>
        </div>
      </section>

      {/* Section 1 — image left, text right */}
      <section className="flex flex-col lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/Girlsinc/IMG_4394.jpeg"
            alt="Girls Inc. students at PS 96"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="lg:w-1/2 bg-[#00213a] flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#fdbb2e] mb-3">About the Program</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-white mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              A Nationally Recognized Program for Girls.
            </h2>
            <p className="text-white/80 leading-relaxed text-lg">
              <strong className="text-white">Girls Inc.</strong> is a nationally recognized program founded on the belief that girls deserve to be strong, smart, and bold. We are proud to offer this empowering initiative at PS 96, providing a safe and nurturing environment where girls can explore their potential, build confidence, and develop essential life skills.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2 — text left, image right */}
      <section className="flex flex-col-reverse lg:flex-row min-h-[460px]">
        <div className="lg:w-1/2 bg-white flex items-center px-10 py-14 lg:px-16">
          <div className="max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#00213a] mb-3">Our Facilitators</p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Led by Ms. Ivonne & Mrs. Desroches.
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg mb-4">
              Facilitated by our School Social Worker <strong className="text-gray-900">Ms. Ivonne</strong> and Elementary School Counselor <strong className="text-gray-900">Mrs. Desroches</strong>, Girls Inc. serves students in grades 3 through 6.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Each session is designed to encourage girls to embrace their individuality while cultivating a sense of community and sisterhood. The program focuses on fostering self-esteem, resilience, and leadership skills through engaging activities and discussions.
            </p>
          </div>
        </div>
        <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden">
          <img
            src="/assets/images/Girlsinc/IMG_0167.jpeg"
            alt="Girls Inc. program at PS 96"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Three Pillars */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/10 rounded-lg border border-[#fdbb2e]/30 mb-4">
              <span className="text-sm font-semibold text-[#00213a]">The Girls Inc. Promise</span>
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
              Strong. Smart. Bold.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((p) => (
              <div key={p.label} className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="w-14 h-14 rounded-2xl bg-[#00213a] flex items-center justify-center mx-auto mb-5">
                  <span className="text-white font-black text-lg">{p.label[0]}</span>
                </div>
                <h3 className="text-xl font-bold text-[#00213a] mb-3">{p.label}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Grid */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-24">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdbb2e]/5 rounded-lg border border-[#fdbb2e]/30 mb-4">
            <span className="text-sm font-semibold text-[#00213a]">What Girls Gain</span>
          </div>
          <h2 className="text-4xl font-semibold text-gray-900 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Skills for Life
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {skills.map((skill) => (
            <div key={skill} className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-[#fdbb2e]/30 transition-colors duration-200">
              <svg className="w-4 h-4 text-[#00213a] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm font-medium text-gray-700">{skill}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#00213a] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6 tracking-tight" style={{ letterSpacing: '-0.02em' }}>
            Empowering Every Girl at PS 96
          </h2>
          <p className="text-xl text-white/80 mb-8">
            For more information about Girls Inc., contact Ms. Ivonne or Mrs. Desroches through the main office.
          </p>
          <a
            href="/contact-us"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#00213a] font-semibold rounded-xl hover:bg-gray-50 transition-colors duration-200 shadow-sm"
          >
            Contact Us <ArrowRightIcon />
          </a>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default GirlsIncPage;
